import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Package, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';

const TrackShipment = () => {
  const navigate = useNavigate();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shipment, setShipment] = useState(null);
  const [searching, setSearching] = useState(false);

  const trackShipment = async () => {
    if (!trackingNumber.trim()) {
      toast.error('Please enter a tracking number');
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shipping/track/${trackingNumber}`);
      
      if (response.ok) {
        const data = await response.json();
        setShipment(data);
      } else {
        toast.error('Shipment not found');
        setShipment(null);
      }
    } catch (error) {
      toast.error('Error tracking shipment');
    } finally {
      setSearching(false);
    }
  };

  const statusColors = {
    'pending_payment': 'bg-[#F59E0B]/20 text-[#F59E0B]',
    'confirmed': 'bg-[#10B981]/20 text-[#10B981]',
    'picked_up': 'bg-[#3B82F6]/20 text-[#3B82F6]',
    'in_transit': 'bg-[#8B5CF6]/20 text-[#8B5CF6]',
    'delivered': 'bg-[#10B981]/20 text-[#10B981]'
  };

  return (
    <div className="min-h-screen bg-[#09090B] p-8" data-testid="track-shipment-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span style={{ fontFamily: 'Manrope' }}>Back to Home</span>
          </button>
          <h1 className="text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Track Shipment</h1>
          <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>Enter your tracking number to see shipment status</p>
        </div>

        {/* Search Box */}
        <div className="card-glass p-8 mb-8">
          <div className="flex gap-4">
            <input
              type="text"
              className="input-base flex-1"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
              placeholder="Enter tracking number (e.g., ZN1234567890)"
              data-testid="tracking-number-input"
              onKeyPress={(e) => e.key === 'Enter' && trackShipment()}
            />
            <button
              onClick={trackShipment}
              disabled={searching}
              className="btn-primary"
              data-testid="track-btn"
            >
              <Search className="w-5 h-5 inline-block mr-2" />
              {searching ? 'Tracking...' : 'Track'}
            </button>
          </div>
        </div>

        {/* Shipment Details */}
        {shipment && (
          <div className="space-y-6">
            {/* Status Card */}
            <div className="card-glass p-8 border-l-4 border-[#FFD60A]">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>{shipment.tracking_number}</h2>
                  <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>{shipment.from_location} → {shipment.to_location}</p>
                </div>
                <span className={`px-4 py-2 uppercase tracking-wider font-semibold ${statusColors[shipment.status] || 'bg-white/10 text-white/60'}`}>
                  {shipment.status.replace('_', ' ')}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-white/60 text-sm mb-1" style={{ fontFamily: 'Manrope' }}>Weight</p>
                  <p className="text-xl font-bold text-white" style={{ fontFamily: 'Sora' }}>{shipment.final_weight.toFixed(2)} lbs</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1" style={{ fontFamily: 'Manrope' }}>Cost</p>
                  <p className="text-xl font-bold text-[#FFD60A]" style={{ fontFamily: 'Sora' }}>${shipment.cost.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1" style={{ fontFamily: 'Manrope' }}>Estimated Delivery</p>
                  <p className="text-xl font-bold text-white" style={{ fontFamily: 'Sora' }}>7 days</p>
                </div>
              </div>
            </div>

            {/* QR Code */}
            {shipment.qr_code && (
              <div className="card-glass p-8 text-center">
                <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Sora' }}>Shipping Label</h3>
                <img src={shipment.qr_code} alt="QR Code" className="mx-auto w-48 h-48" data-testid="qr-code" />
                <p className="text-white/60 text-sm mt-4" style={{ fontFamily: 'Manrope' }}>Scan this code for quick tracking</p>
              </div>
            )}

            {/* Tracking Events */}
            {shipment.tracking_events && shipment.tracking_events.length > 0 && (
              <div className="card-glass p-8">
                <h3 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Sora' }}>Tracking History</h3>
                <div className="space-y-4">
                  {shipment.tracking_events.map((event, index) => (
                    <div key={index} className="flex gap-4 pb-4 border-b border-white/10 last:border-0" data-testid={`event-${index}`}>
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-[#FFD60A]/10 border border-[#FFD60A]/30 flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-[#FFD60A]" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold mb-1" style={{ fontFamily: 'Manrope' }}>{event.status.replace('_', ' ')}</p>
                        <p className="text-white/60 text-sm" style={{ fontFamily: 'Manrope' }}>{event.location}</p>
                        <p className="text-white/40 text-xs mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(event.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Addresses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card-glass p-6">
                <h4 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Sora' }}>Pickup Address</h4>
                <p className="text-white/80" style={{ fontFamily: 'Manrope' }}>{shipment.pickup_address}</p>
              </div>
              <div className="card-glass p-6">
                <h4 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Sora' }}>Delivery Address</h4>
                <p className="text-white/80" style={{ fontFamily: 'Manrope' }}>{shipment.delivery_address}</p>
              </div>
            </div>
          </div>
        )}

        {!shipment && !searching && (
          <div className="card-glass p-12 text-center">
            <Package className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>Enter a tracking number to view shipment details</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackShipment;